import { signOut } from "aws-amplify/auth";
import LogRocket from "logrocket";

import gql from "../gqlTag";
import { snackbarError } from "../snackbar/snackbar-actions";
import { callGqlApi, setApiWorkspace } from "../../helpers/api";
import history from "../../helpers/history";
import { userDisplayName } from "../../helpers/common";
import { setGlobalUTCOffset } from "../../helpers/time";
import { selectWorkspace } from "../workspace/workspace-actions";
import { loadDataAsNeeded as loadRxDiagDataAsNeeded } from "../rxdiag/rxdiag-actions";

import { Heap } from "./heap";
import { handleGqlErrors } from "../gql-error/gql-error-actions";
import { CognitoUser, GqlUser, GqlUserQueryData, UserAction } from "./user-types";
import { Thunk } from "../common";
import { SnackBarAction } from "../snackbar/snackbar-types";

declare global {
  interface Window {
    heap: Heap;
  }
}

export const useTimeCorrectionStorageKey = "useTimeCorrection";

const userQuery = gql`
  {
    currentUser {
      id
      sysproContactId
      email
      givenName
      nickname
      familyName
      acceptedPrivacyPolicy
      isExternal
      workspaceInvites {
        id
        message
        isOwner
        sender {
          email
          givenName
          familyName
        }
        status
        workspace {
          id
          name
          description
        }
      }
      newFeatures {
        id
        title
        htmlBody
        componentKey
      }
    }
    allNewFeatures {
      id
      title
      htmlBody
      componentKey
    }
  }
`;

// Typed wrapper around the callGqlApi helper specific to the user query
function callGqlUserQuery(): Promise<GqlUserQueryData> {
  return callGqlApi(userQuery);
}

// Transform the data we get back from the gql user query into the shape stored in the
// redux store. (In reality it is also merged with the cognito user data.)
function reshapeGqlUserData(data: GqlUserQueryData): GqlUser {
  return {
    ...data.currentUser,
    displayName: userDisplayName(data.currentUser),
    allNewFeatures: data.allNewFeatures,
  };
}

export function selectOffset(offsetName: string, reloadWorkspace = true): Thunk<void, UserAction> {
  return (dispatch, getState) => {
    setGlobalUTCOffset(offsetName);
    localStorage.setItem("offsetPreference", offsetName);
    dispatch({
      type: "USER_CHANGE_OFFSET",
      payload: { offset: offsetName },
    });
    if (
      reloadWorkspace &&
      !history.location.pathname.includes("/detection-map") &&
      !history.location.pathname.includes("/rx-diag")
    ) {
      const workspaceId = getState().workspaces.selectedWorkspace!.id;
      dispatch(selectWorkspace(workspaceId, history.location.pathname));
    }
  };
}

export function setUseTimeCorrection(useTimeCorrection: boolean) {
  return dispatch => {
    localStorage.setItem(useTimeCorrectionStorageKey, useTimeCorrection.toString());
    dispatch({
      type: "USER_TOGGLE_TIME_CORRECTION",
      payload: { useTimeCorrection },
    });
    dispatch(loadRxDiagDataAsNeeded());
  };
}

function logRocketIdentify(cognitoUser: CognitoUser) {
  LogRocket.identify(cognitoUser.email, {
    email: cognitoUser.email,
  });
}

export function setCurrentUser(
  cognitoUser: CognitoUser
): Thunk<Promise<void> | undefined, UserAction | SnackBarAction> {
  return dispatch => {
    const cognitoGroups = cognitoUser.session.tokens?.accessToken.payload?.["cognito:groups"];
    const isAdmin = Boolean(
      cognitoGroups && Array.isArray(cognitoGroups) && cognitoGroups.includes("admin")
    );

    if (window.heap?.appid) {
      // identify user to heap (but only if heap is loaded)
      window.heap.identify(cognitoUser.email);
    }
    const storedUseTimeCorrection = localStorage.getItem(useTimeCorrectionStorageKey) ?? "true";
    dispatch(setUseTimeCorrection(storedUseTimeCorrection === "true"));
    return dispatch(addGqlDataAndSetCurrentUser(cognitoUser, isAdmin));
  };
}

// This function takes the user data from cognito, and then calls the GQL server to
// get the user data from there, then combines the two and dispatches a redux action
// to store the result.
export function addGqlDataAndSetCurrentUser(
  cognitoUser: CognitoUser,
  isAdmin: boolean
): Thunk<Promise<void>, UserAction | SnackBarAction> {
  return dispatch => {
    return callGqlUserQuery()
      .then(gqlUserData => {
        dispatch({
          type: "USER_SET_CURRENT",
          payload: {
            user: {
              ...cognitoUser,
              ...reshapeGqlUserData(gqlUserData),
            },
            isAdmin,
          },
        });
        // Only load what's new if there are things new to them and don't load on detections if redirected from what's new
        const qParams = new URLSearchParams(history.location.search);
        if (
          gqlUserData.currentUser.newFeatures.length > 0 &&
          !(history.location.pathname.includes("/detections") && qParams.get("whats-new"))
        ) {
          dispatch(viewWhatsNew());
        }
        const offsetPreference = localStorage.getItem("offsetPreference");
        dispatch(selectOffset(offsetPreference || "UTC", false));
        // Capture sign-in event and send user data to logrocket:
        logRocketIdentify(cognitoUser);
      })
      .catch(error => {
        // just set the cognito info - cannot reach gql server and redirect to error page
        dispatch({
          type: "USER_SET_CURRENT",
          payload: { user: { ...cognitoUser }, isAdmin },
        });
        logRocketIdentify(cognitoUser);
        console.error(error);
        history.push("/error");
      });
  };
}

export function logout(): Thunk<void, UserAction | SnackBarAction> {
  return dispatch => {
    // amplify will redirect to sign in on completion
    signOut()
      .then(() => {
        localStorage.clear();
        setApiWorkspace("");
        window.location.reload();
      })
      .catch(err => {
        console.error(err);
        dispatch(snackbarError("Error Signing Out"));
      });
  };
}

export function refreshUserData(): Thunk<Promise<void>, UserAction | SnackBarAction> {
  return dispatch => {
    return callGqlUserQuery()
      .then((data: GqlUserQueryData) => {
        dispatch({
          type: "USER_REFRESH_DATA",
          payload: {
            user: reshapeGqlUserData(data),
          },
        });
      })
      .catch(error => {
        console.error(error);
        dispatch(snackbarError("Error refreshing user data"));
      });
  };
}

export function openAcceptPP(): Thunk<void, UserAction> {
  return dispatch => {
    dispatch({
      type: "USER_ACCEPT_PRIVACY_POLICY",
    });
  };
}

export function openAccept(): Thunk<void, UserAction> {
  return dispatch => {
    dispatch({
      type: "USER_ACCEPT_WHATS_NEW",
    });
  };
}

export function openViewPP(): Thunk<void, UserAction> {
  return dispatch => {
    dispatch({
      type: "USER_VIEW_PRIVACY_POLICY",
    });
  };
}

export function closeWhatsNew(): Thunk<void, UserAction> {
  return dispatch => {
    dispatch({
      type: "USER_CLOSE_WHATS_NEW",
    });
    callGqlApi(gql`
      mutation {
        viewedAllNewFeatures
      }
    `).catch(e => handleGqlErrors(e));
  };
}

export function closePP(): Thunk<void, UserAction> {
  return dispatch => {
    dispatch({
      type: "USER_CLOSE_PRIVACY_POLICY",
    });
  };
}

export function viewWhatsNew(): Thunk<void, UserAction> {
  return dispatch => {
    dispatch({
      type: "USER_VIEW_WHATS_NEW",
    });
  };
}

export function reviewWhatsNew(): Thunk<void, UserAction> {
  return dispatch => {
    dispatch({
      type: "USER_REVIEW_WHATS_NEW",
    });
  };
}
